// src/pages/Accounting/Reports/AccountList/components/EmailModal.tsx
// Send Account List modal: To / Cc / Subject / Format / Message / FileName.

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { EmailParams } from '../types';
import { buildEmailDefaults } from '../utils';

interface EmailModalProps {
  open: boolean;
  onClose: () => void;
  onSend: (params: EmailParams) => void;
  reportName: string;
  companyName: string;
  userName: string | null;
}

export default function EmailModal({ open, onClose, onSend, reportName, companyName, userName }: EmailModalProps) {
  const [params, setParams] = useState<EmailParams>(() => buildEmailDefaults(reportName, companyName, userName));

  useEffect(() => {
    if (open) setParams(buildEmailDefaults(reportName, companyName, userName));
  }, [open, reportName, companyName, userName]);

  if (!open) return null;

  const update = (patch: Partial<EmailParams>) => setParams((p) => ({ ...p, ...patch }));
  const extensionFor = (f: EmailParams['format']) => (f === 'xlsx' ? '.xlsx' : f === 'csv' ? '.csv' : '.pdf');

  return createPortal(
    <div onClick={onClose} className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-line rounded-xl shadow-2xl p-6 w-full max-w-[520px]">
        <div className="relative mb-4">
          <h4 className="text-[17px] font-extrabold text-ink text-center m-0">Send {reportName}</h4>
          <button type="button" onClick={onClose} aria-label="Close" className="absolute right-0 top-0 text-ink3 cursor-pointer border-none bg-transparent hover:text-ink">
            <X size={18} />
          </button>
        </div>

        <Row label="To">
          <input value={params.to} onChange={(e) => update({ to: e.target.value })} placeholder="Separate multiple emails with commas" className="w-full bg-white border border-line rounded-md px-2.5 py-1.5 text-[12px] text-ink placeholder:text-ink4 outline-none focus:border-teal-dark" />
        </Row>
        <Row label="Cc">
          <input value={params.cc} onChange={(e) => update({ cc: e.target.value })} placeholder="Separate multiple emails with commas" className="w-full bg-white border border-line rounded-md px-2.5 py-1.5 text-[12px] text-ink placeholder:text-ink4 outline-none focus:border-teal-dark" />
        </Row>
        <Row label="Subject">
          <input value={params.subject} onChange={(e) => update({ subject: e.target.value })} className="w-full bg-white border border-line rounded-md px-2.5 py-1.5 text-[12px] text-ink outline-none focus:border-teal-dark" />
        </Row>
        <Row label="Format">
          <div className="flex gap-5">
            {(['xlsx', 'csv', 'pdf'] as const).map((f) => (
              <label key={f} className="inline-flex items-center gap-1.5 text-[12px] text-ink cursor-pointer">
                <input type="radio" checked={params.format === f} onChange={() => update({ format: f })} className="accent-teal-dark" />
                {f === 'xlsx' ? 'Excel' : f.toUpperCase()}
              </label>
            ))}
          </div>
        </Row>
        <Row label="Message">
          <textarea value={params.message} onChange={(e) => update({ message: e.target.value })} rows={5} className="w-full bg-white border border-line rounded-md px-2.5 py-1.5 text-[12px] text-ink outline-none focus:border-teal-dark font-sans resize-y" />
        </Row>
        <div className="grid grid-cols-[72px_1fr_auto] gap-x-3.5 gap-y-2.5 items-center mb-4">
          <label className="text-[12px] text-ink2 font-semibold">FileName</label>
          <input value={params.fileName} onChange={(e) => update({ fileName: e.target.value })} className="bg-white border border-line rounded-md px-2.5 py-1.5 text-[12px] text-ink outline-none focus:border-teal-dark" />
          <span className="text-[12px] text-ink3">{extensionFor(params.format)}</span>
        </div>

        <div className="flex justify-between items-center pt-3 border-t border-lineSoft">
          <button type="button" onClick={onClose} className="text-ink3 text-[12.5px] font-bold px-4 py-2 border-none bg-transparent cursor-pointer hover:text-ink">
            Cancel
          </button>
          <button type="button" onClick={() => onSend(params)} className="bg-teal-dark text-white text-[12.5px] font-bold px-5 py-2 rounded-md border-none cursor-pointer hover:brightness-110">
            Send email
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[72px_1fr] gap-x-3.5 gap-y-2.5 items-center mb-3">
      <label className="text-[12px] text-ink2 font-semibold">{label}</label>
      <div>{children}</div>
    </div>
  );
}